/**
 * CaptureProvider — abstraction over the screenshot capture mechanism.
 *
 * The web build uses `html2canvas-pro` (NOT vanilla html2canvas) to
 * rasterize the visible viewport out of the DOM. Argus ships Tailwind 4,
 * whose design tokens emit `oklch()`, `oklab()`, `lab()`, and
 * `color-mix()` colors throughout the UI. Vanilla html2canvas 1.4.x
 * throws on those modern color functions, which is what surfaced as the
 * "Screenshot capture failed" banner on every page. `html2canvas-pro`
 * is a drop-in API-compatible fork that handles them natively.
 *
 * A future Tauri/Electron wrapper can implement the same interface
 * against the platform's native screen-capture API (and get higher-
 * fidelity captures of non-DOM content like Monaco / WebGL canvases)
 * without touching the modal code.
 *
 * The provider MUST stay browser-agnostic at the type level — no
 * Electron-only types. Implementations choose how to satisfy the
 * `CaptureResult` contract.
 */

export interface CaptureResult {
  /** PNG or JPEG, base64-encoded WITHOUT the `data:image/...;base64,` prefix. */
  base64: string;
  mimeType: 'image/png' | 'image/jpeg';
  /** Byte size of the decoded image (post-compression). */
  byteSize: number;
  /** Pixel dimensions of the captured image. */
  width: number;
  height: number;
}

export interface CaptureProvider {
  /** Capture the visible viewport. Throws on failure. */
  captureViewport(): Promise<CaptureResult>;
}

// ─── html2canvas web implementation ──────────────────────────────────────────

/** Max bytes for the on-wire payload. Anything bigger is recompressed. */
export const MAX_SCREENSHOT_BYTES = 40 * 1024;
/** Max width we'll send. Anything wider is resized down. */
export const MAX_SCREENSHOT_WIDTH = 1280;

/**
 * Compress a canvas down to fit within `MAX_SCREENSHOT_BYTES`.
 *
 * Strategy:
 *   1. Try PNG. If small enough, ship it.
 *   2. Otherwise, ship JPEG at quality 0.75. (Most viewport screenshots
 *      come in well under 40 KB at that level.)
 *   3. If even JPEG @ 0.75 is too big, drop quality to 0.55 once.
 *
 * Returns the chosen base64 + mime + computed byte size.
 */
function compressCanvas(canvas: HTMLCanvasElement): CaptureResult {
  const pngDataUrl = canvas.toDataURL('image/png');
  const pngBytes = estimateBytes(pngDataUrl);
  if (pngBytes <= MAX_SCREENSHOT_BYTES) {
    return {
      base64: stripDataUrlPrefix(pngDataUrl),
      mimeType: 'image/png',
      byteSize: pngBytes,
      width: canvas.width,
      height: canvas.height,
    };
  }
  const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.75);
  const jpegBytes = estimateBytes(jpegDataUrl);
  if (jpegBytes <= MAX_SCREENSHOT_BYTES) {
    return {
      base64: stripDataUrlPrefix(jpegDataUrl),
      mimeType: 'image/jpeg',
      byteSize: jpegBytes,
      width: canvas.width,
      height: canvas.height,
    };
  }
  const jpegLowDataUrl = canvas.toDataURL('image/jpeg', 0.55);
  return {
    base64: stripDataUrlPrefix(jpegLowDataUrl),
    mimeType: 'image/jpeg',
    byteSize: estimateBytes(jpegLowDataUrl),
    width: canvas.width,
    height: canvas.height,
  };
}

function stripDataUrlPrefix(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/** Approx decoded byte size of a base64 data URL. */
function estimateBytes(dataUrl: string): number {
  const stripped = stripDataUrlPrefix(dataUrl);
  // Base64 expands 3 bytes -> 4 chars, plus ~2 bytes of padding.
  return Math.floor((stripped.length * 3) / 4);
}

/**
 * Default web provider. Lazily imports html2canvas-pro so the dependency
 * is only loaded when the user actually clicks the bug-report button
 * (keeps the main bundle smaller).
 */
export const htmlCaptureProvider: CaptureProvider = {
  async captureViewport(): Promise<CaptureResult> {
    if (typeof document === 'undefined') {
      throw new Error('Screenshot capture is only available in a browser.');
    }
    const html2canvas = (await import('html2canvas-pro')).default;

    // Capture the visible portion of the page, not the full scroll
    // height. We use the document scroll offsets rather than 0/0 so
    // the user sees what they were looking at when they clicked +.
    const x = window.scrollX;
    const y = window.scrollY;
    const width = Math.min(window.innerWidth, MAX_SCREENSHOT_WIDTH);
    const height = Math.round(
      (width / window.innerWidth) * window.innerHeight,
    );

    const canvas = await html2canvas(document.documentElement, {
      x,
      y,
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      // html2canvas can't paint cross-origin images without CORS; let
      // them silently degrade rather than aborting the whole capture.
      useCORS: true,
      allowTaint: false,
      // Avoid stripping our brand colors by default — html2canvas
      // sometimes injects a white background otherwise.
      backgroundColor: null,
      logging: false,
      scale: width / window.innerWidth,
    });

    // If html2canvas honored `scale` we're already at <= MAX_WIDTH.
    // Either way, the compression step ensures the wire payload is
    // ≤ MAX_SCREENSHOT_BYTES.
    // (Width var kept above for the scale calc; unused after.)
    void height;
    return compressCanvas(canvas);
  },
};
